import { type ReactElement } from "react";
import type {
  CountermeasureStatus,
  CountermeasureTableParams,
} from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import type { WidgetBodyProps } from "../types.js";

/* Kept hand-built (design-system adoption): StatusPill's healthy fill is the
   soft token (12%, 40% in high contrast), not Done's 15% green, and no option
   draws it. The swap to StatusPill (look row OK-15) waits for the design
   owner, so every status keeps today's chip. */
const STATUS_TONE: Record<CountermeasureStatus, string> = {
  Proposed: "bg-pb-card-elevated text-pb-text-muted border-pb-border",
  Approved: "bg-pb-cyan-soft text-pb-cyan-text border-pb-cyan/40",
  "In Design": "bg-pb-purple-soft text-pb-purple-text border-pb-purple/40",
  Trial: "bg-pb-amber-soft text-pb-amber-text border-pb-amber/40",
  Done: "bg-pb-green/15 text-pb-green-text border-pb-green/40",
};

export function CountermeasureTableWidget({
  data,
}: WidgetBodyProps<CountermeasureTableParams>): ReactElement {
  return <CountermeasureList data={data} />;
}

export function CountermeasureList({
  data,
  selectedId,
  onSelect,
  selectionLocked = false,
}: {
  data: CountermeasureTableParams;
  selectedId?: string | undefined;
  onSelect?: ((id: string) => void) | undefined;
  selectionLocked?: boolean;
}): ReactElement {
  if (!data.rows.length)
    return (
      <p className="p-4 text-sm text-pb-text-muted">
        No countermeasures yet — expand to add one.
      </p>
    );
  return (
    <ul
      aria-label="Countermeasures"
      className="min-h-0 min-w-0 flex-1 overflow-auto divide-y divide-pb-border"
    >
      {data.rows.map((row) => {
        const content = (
          <>
            <div className="min-w-0 flex-1">
              <p className="break-words text-sm font-medium text-pb-text">
                {row.title || "Untitled countermeasure"}
              </p>
              <p className="mt-1 break-words text-xs text-pb-text-muted">
                {row.owner || "Owner not set"} ·{" "}
                {row.window || "Time window not set"}
              </p>
            </div>
            <span
              className={cn(
                "shrink-0 rounded-md border px-2 py-1 text-xs",
                STATUS_TONE[row.status],
              )}
            >
              {row.status}
            </span>
          </>
        );
        return (
          <li key={row.id}>
            {onSelect ? (
              <button
                type="button"
                aria-pressed={selectedId === row.id}
                disabled={selectionLocked}
                onClick={() => onSelect(row.id)}
                className={cn(
                  "flex w-full items-start gap-3 rounded-md border border-transparent p-3 text-start focus-ring disabled:cursor-not-allowed",
                  selectedId === row.id
                    ? "border-pb-teal bg-pb-teal/5"
                    : "hover:pb-overlay-paint",
                )}
              >
                {content}
              </button>
            ) : (
              <div className="flex items-start gap-3 py-3 px-1">{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
